import React, { useEffect, useState, useRef } from "react";
import BCMenuBar from "../../ui/BCMenuBar";
import { GlobalConstants } from "../../constants/GlobalConstants";
import { SemanticUIDropdownOptionsType } from "./LmsFormGenerator";
import { getDropdownSemanticOptionsFromData, getParsedPropItemFromLegacy, isJson, startsWithCapital } from "../../../helpers/GlobalHelper";
import { Dropdown, Button, Icon, Search } from "semantic-ui-react";
import LmsTableFilterSubmitModal from "./LmsTableFilterSubmitModal";
import { convertCamelCase } from "../../../Helpers";

interface ILmsFilterSearchBarProps {
  searchBarLeftComponent?: JSX.Element;
  tableFilterReducerInstance?: [any, (any) => any];
  handleOpenFilterSubmitClick: (e: any, dropdownOptionsRef: any) => void
  tableApiSuffix?: string;
  apiFiltering?: boolean;
  settingOptions?: any[]
  isSettingsOptionsLoading?: boolean;
  settingsOptionsError?: any;
  filterSubmitModalOptions?: any
  isLoaderVisible?: boolean;
}

const LmsFilterSearch = ({ tableFilterReducerInstance, apiFiltering = false }) => {
  const [{ searchValue, setSearchValue }]: any = tableFilterReducerInstance;
  const [searchValueState, setSearchValueState] = useState(searchValue);
  const [searchSubmitTimer, setSearchSubmitTimer] = useState(null as any);

  useEffect(() => {
    if (searchValueState !== searchValue) {
      setSearchValueState(searchValue);
    }
  }, [searchValue]);

  useEffect(() => {
    if (searchValueState !== searchValue) {
      const debounceTime = apiFiltering ? 1000 : 300;
      if (searchSubmitTimer) {
        clearTimeout(searchSubmitTimer);
      }

      setSearchSubmitTimer(setTimeout(() => {
        setSearchValue(searchValueState);
      }, debounceTime));

      return () => clearTimeout(searchSubmitTimer);
    }
  }, [searchValueState]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchValueState(value);
  };

  const handleEnterPress = (e) => {
    if (e.key === "Enter") {
      if (searchSubmitTimer) {
        clearTimeout(searchSubmitTimer);
      }

      setSearchValue(searchValueState);
    }
  };

  return (
    <div className="filter_search_container">
      <Search
        open={false}
        onSearchChange={handleSearchChange}
        placeholder="Filter by keyword"
        value={searchValueState}
        onKeyPress={handleEnterPress}
      />
    </div>
  );
};

interface LmsFilterViewDropdownDrops {
  formattedOptions?: SemanticUIDropdownOptionsType,
  tableFilterReducerInstance?: [any, (any) => any],
  handleOpenFilterSubmitClick: (e: any, dropdownOptionsRef: any) => void
  settingOptions?: any[],
  isSettingsOptionsLoading?: boolean,
  settingsOptionsError?: any,
  filterSubmitModalOptions?: any
  isLoaderVisible?: boolean;
}

const LmsFilterViewDropdown = (
  {
    tableFilterReducerInstance,
    handleOpenFilterSubmitClick,
    settingOptions,
    isSettingsOptionsLoading,
    settingsOptionsError,
    filterSubmitModalOptions,
    isLoaderVisible = false,
  }: LmsFilterViewDropdownDrops) => {
  const [{ currentViewValue, currentViewFilters, isFilteringDisabled, allViewOptions }, dispatch]: any = tableFilterReducerInstance;
  const [options, setOptions] = useState(undefined as SemanticUIDropdownOptionsType | undefined);
  const hasActiveFilters = currentViewFilters?.filters?.length > 0;
  const dropdownOptionsRef = useRef(null as any);
  const handleChange = (_, { value }: any) => {
    dispatch({
      type: GlobalConstants.LmsTableReducerCases.SET_CURRENT_VIEW_VALUE,
      payload: value,
    });
  };

  const formatColumnFilter = (fieldName, columnFilter) => {
    return startsWithCapital(fieldName) ? { ...columnFilter, field: getParsedPropItemFromLegacy(fieldName)?.prop || convertCamelCase(fieldName) } : columnFilter;
  };

  const getFormattedColumnFilter = (columnFilter) => {
    if (columnFilter?.field) {
      return formatColumnFilter(columnFilter.field, columnFilter);
    }
    else if (columnFilter?.filters?.[0]?.field) {
      return {
        ...columnFilter,
        filters: columnFilter?.filters?.map(filter => formatColumnFilter(filter?.field, filter))
      };
    }
  };

  const parseFiltersStr = (jsonStr) => {
    if (jsonStr && isJson(jsonStr)) {
      const parsedFilters = jsonStr && isJson(jsonStr) ? JSON.parse(jsonStr) : [];
      if (parsedFilters?.filters) {
        if (parsedFilters?.filters?.[0]?.field) {
          return { filters: [getFormattedColumnFilter(parsedFilters)] };
        }
        else {
          return { filters: parsedFilters?.filters?.map(columnFilter => getFormattedColumnFilter(columnFilter)) };
        }
      }
    }

    return {};
  };

  const getFormattedSettingOptions = () => {
    if (settingOptions) {
      return settingOptions?.map((option) => {
        return {
          ...option,
          filters: JSON.stringify(parseFiltersStr(option.filters)),
        };
      });
    }
  };

  useEffect(() => {
    if (settingOptions || settingsOptionsError) {
      if (!isSettingsOptionsLoading) {
        if (!settingsOptionsError) {
          dispatch({
            type: GlobalConstants.LmsTableReducerCases.SET_ALL_VIEW_OPTIONS,
            payload: getFormattedSettingOptions() || []
          });

          setOptions(getDropdownSemanticOptionsFromData(settingOptions || []));
        }
        else {
          setOptions([]);
          dispatch({
            type: GlobalConstants.LmsTableReducerCases.SET_ALL_VIEW_OPTIONS,
            payload: []
          });
        }
      }
    }
  }, [settingOptions, settingsOptionsError, isSettingsOptionsLoading]);

  useEffect(() => {
    if (allViewOptions && setOptions && allViewOptions.length !== setOptions.length) {
      setOptions(getDropdownSemanticOptionsFromData(allViewOptions || []));
    }
  }, [allViewOptions]);

  const toggleFiltering = () => {
    dispatch({
      type: GlobalConstants.BCStoreReducerCases.TOGGLE_FILTERING_DISABLED,
    });
  };

  return (
    <div className="lms-table-filter-views-dropdown-container">
      <div
        className="lms-table-toggle-filter-button-container"
        onClick={toggleFiltering}
      >
        <Icon name="filter" disabled={!hasActiveFilters || isFilteringDisabled} color={hasActiveFilters && !isFilteringDisabled ? "red" : "grey"} />
      </div>
      <div className="lms-table-filter-views-dropdown" ref={dropdownOptionsRef}>
        <Dropdown
          options={options || []}
          selection
          loading={isSettingsOptionsLoading}
          value={currentViewValue}
          onChange={handleChange}
          className="lms-table-action-dropdown-button"
          clearable
          disabled={!options || options?.length === 0}
        />
        <Button
          size="big"
          className="dropdown-options-button"
          icon={GlobalConstants.Icons.SETTING}
          onClick={(e) => handleOpenFilterSubmitClick(e, dropdownOptionsRef)}
          disabled={isSettingsOptionsLoading}
        />
      </div>
      <LmsTableFilterSubmitModal
        tableFilterReducerInstance={tableFilterReducerInstance}
        isLoaderVisible={isLoaderVisible}
        {...filterSubmitModalOptions}
      />
    </div>
  );
};

const LmsFilterSearchBar = (
  {
    tableFilterReducerInstance,
    searchBarLeftComponent,
    handleOpenFilterSubmitClick,
    apiFiltering,
    settingOptions,
    isSettingsOptionsLoading,
    settingsOptionsError,
    filterSubmitModalOptions,
    isLoaderVisible
  }: ILmsFilterSearchBarProps) => {
  return (
    <BCMenuBar
      leftChildren={[searchBarLeftComponent || <React.Fragment />]}
      rightChildren={[
        <LmsFilterViewDropdown
          tableFilterReducerInstance={tableFilterReducerInstance}
          handleOpenFilterSubmitClick={handleOpenFilterSubmitClick}
          settingOptions={settingOptions}
          isSettingsOptionsLoading={isSettingsOptionsLoading}
          settingsOptionsError={settingsOptionsError}
          filterSubmitModalOptions={filterSubmitModalOptions}
          isLoaderVisible={isLoaderVisible}
        />,
        <LmsFilterSearch tableFilterReducerInstance={tableFilterReducerInstance} apiFiltering={apiFiltering} />
      ]}
    />
  );
};

export default LmsFilterSearchBar;